import React from 'react';
import { Link } from 'react-router-dom';
import config from '../../config.json';
import Shot from './Shot';
import { HERO_IMAGE, PAYMENT_METHODS, SECTIONS } from './featuresData';
// import HERO_IMAGE from '../../assets/Hero-image.png';
import './features.css';

const REGISTER_URL = `${config.CUSTOMER_WEBSITE_URL}/register`;

function Spotlight({ title, body, points, image, imageLabel, link }) {
    return (
        <article className="wqf-spot">
            <div className="wqf-spot-copy">
                <h3 className="wqf-h3">{title}</h3>
                <p className="wqf-body">{body}</p>
                {points && (
                    <ul className="wqf-points">
                        {points.map((p) => (
                            <li key={p}>{p}</li>
                        ))}
                    </ul>
                )}
                {link && (
                    <Link to={link.to} className="wqf-link">
                        {link.label}
                    </Link>
                )}
            </div>
            <Shot src={image} label={imageLabel} />
        </article>
    );
}

function ItemGrid({ items }) {
    return (
        <ul className="wqf-items">
            {items.map((item) => (
                <li key={item.title}>
                    <h4 className="wqf-h4">{item.title}</h4>
                    <p className="wqf-small">{item.body}</p>
                </li>
            ))}
        </ul>
    );
}

function Methods() {
    return (
        <div className="wqf-methods-block">
            <h3 className="wqf-h3">Five ways to pay</h3>
            <ul className="wqf-methods">
                {PAYMENT_METHODS.map(([name, desc]) => (
                    <li key={name}>
                        <span className="wqf-method-name">{name}</span>
                        <span className="wqf-small">{desc}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Integrations({ items }) {
    return (
        <div className="wqf-integrations">
            <div className="wqf-keys" aria-label="API key format">
                <p className="wqf-small">Your secret keys tell you which mode you’re in:</p>
                <code>WAYASECK_TEST_…</code>
                <code>WAYASECK_PROD_…</code>
            </div>
            <ul className="wqf-int-list">
                {items.map((item) => (
                    <li key={item.title}>
                        <Link to={item.to} className="wqf-int">
                            <span className="wqf-h4">{item.title}</span>
                            <span className="wqf-small">{item.body}</span>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Section({ section }) {
    const { id, title, intro, spotlights, items, methods, integrations, link, dark } = section;
    return (
        <section id={id} className={`wqf-section${dark ? ' wqf-dark' : ''}`} aria-labelledby={`${id}-title`}>
            <div className="wqf-wrap">
                <header className="wqf-section-head">
                    <h2 id={`${id}-title`} className="wqf-h2">
                        {title}
                    </h2>
                    <p className="wqf-lead">{intro}</p>
                </header>

                {spotlights?.map((s) => (
                    <Spotlight key={s.title} {...s} />
                ))}
                {methods && <Methods />}
                {integrations && <Integrations items={integrations} />}
                {items && <ItemGrid items={items} />}
                {link && (
                    <p className="wqf-section-link">
                        <Link to={link.to} className="wqf-link">
                            {link.label}
                        </Link>
                    </p>
                )}
            </div>
        </section>
    );
}

function Features() {
    return (
        <main className="wqf">
            <header className="wqf-hero">
                <div className="wqf-wrap wqf-hero-grid">
                    <div className="wqf-hero-copy">
                        <h1 className="wqf-h1">Collect payments, get settled and run your business from one dashboard</h1>
                        <p className="wqf-lead">
                            WayaQuick gives your business payment links, invoices, subscriptions and a secure checkout,
                            with fast settlement to your bank account and the tools to manage customers, payouts and
                            your team.
                        </p>
                        <div className="wqf-ctas">
                            <a href={REGISTER_URL} className="wqf-btn wqf-btn-primary">
                                Create an account
                            </a>
                            <Link to="/get-started" className="wqf-btn wqf-btn-ghost">
                                Read the docs
                            </Link>
                        </div>
                        <ul className="wqf-facts">
                            <li>
                                <strong>5</strong> ways for customers to pay
                            </li>
                            <li>
                                <strong>Instant</strong> settlement available
                            </li>
                            <li>
                                <strong>Free</strong> Test mode to try everything
                            </li>
                        </ul>
                    </div>
                    <Shot src={HERO_IMAGE} label="WayaQuick dashboard" priority />
                </div>
            </header>

            <nav className="wqf-jump" aria-label="Features">
                <ul className="wqf-wrap">
                    {SECTIONS.map((s) => (
                        <li key={s.id}>
                            <a href={`#${s.id}`}>
                                <span className="wqf-jump-name">{s.nav}</span>
                                <span className="wqf-jump-hint">{s.navHint}</span>
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>

            {SECTIONS.map((s) => (
                <Section key={s.id} section={s} />
            ))}

            <section className="wqf-cta" aria-labelledby="wqf-cta-title">
                <div className="wqf-wrap wqf-cta-inner">
                    <div>
                        <h2 id="wqf-cta-title" className="wqf-h2">
                            Start in Test mode today
                        </h2>
                        <p className="wqf-lead">
                            Create an account and try payment links, invoices and integrations with test money. Go live
                            as soon as your KYC is approved.
                        </p>
                    </div>
                    <div className="wqf-ctas">
                        <a href={REGISTER_URL} className="wqf-btn wqf-btn-primary">
                            Create an account
                        </a>
                        <Link to="/pricing" className="wqf-btn wqf-btn-ghost">
                            See pricing
                        </Link>
                        <Link to="/contact" className="wqf-btn wqf-btn-ghost">
                            Talk to us
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}

export default Features;
